import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getCategories, 
  createCategory, 
  updateCategory, 
  deleteCategory,
  getSubcategories,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Edit, 
  Trash2,
  Tags,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export const CategoriesPage = () => {
  const navigate = useNavigate();
  const { isLeader } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Category modal
  const [showCatModal, setShowCatModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', description: '' });
  
  // Subcategory modal
  const [showSubModal, setShowSubModal] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [subForm, setSubForm] = useState({ category_id: '', name: '', description: '' });
  
  // Delete confirmations
  const [deleteCatId, setDeleteCatId] = useState(null);
  const [deleteSubId, setDeleteSubId] = useState(null);

  useEffect(() => {
    if (!isLeader()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isLeader, navigate]);

  const fetchData = async () => {
    try {
      const [catRes, subRes] = await Promise.all([
        getCategories(),
        getSubcategories()
      ]);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const openCatModal = (cat = null) => {
    if (cat) {
      setEditingCat(cat);
      setCatForm({ name: cat.name, description: cat.description });
    } else {
      setEditingCat(null);
      setCatForm({ name: '', description: '' });
    }
    setShowCatModal(true);
  };

  const handleSaveCat = async () => {
    if (!catForm.name) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editingCat) {
        await updateCategory(editingCat.id, catForm);
        toast.success('Category updated');
      } else {
        await createCategory(catForm);
        toast.success('Category created');
      }
      setShowCatModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleDeleteCat = async () => {
    if (!deleteCatId) return;
    try {
      await deleteCategory(deleteCatId);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    } finally {
      setDeleteCatId(null);
    }
  };

  // Subcategory handlers
  const openSubModal = (sub = null) => {
    if (sub) {
      setEditingSub(sub);
      setSubForm({ category_id: sub.category_id, name: sub.name, description: sub.description });
    } else {
      setEditingSub(null);
      setSubForm({ category_id: '', name: '', description: '' });
    }
    setShowSubModal(true);
  };

  const handleSaveSub = async () => {
    if (!subForm.category_id || !subForm.name) {
      toast.error('Category and name are required');
      return;
    }
    try {
      if (editingSub) {
        await updateSubcategory(editingSub.id, subForm);
        toast.success('Subcategory updated');
      } else {
        await createSubcategory(subForm);
        toast.success('Subcategory created');
      }
      setShowSubModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save subcategory');
    }
  };

  const handleDeleteSub = async () => {
    if (!deleteSubId) return;
    try {
      await deleteSubcategory(deleteSubId);
      toast.success('Subcategory deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete subcategory');
    } finally {
      setDeleteSubId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-imperial" />
      </div>
    );
  }

  return (
    <div data-testid="categories-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-imperial">Categories & Subcategories</h1>
        <p className="font-body text-muted-foreground mt-1">
          Manage budget item categories
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="categories">
        <TabsList>
          <TabsTrigger value="categories" data-testid="categories-tab">Categories</TabsTrigger>
          <TabsTrigger value="subcategories" data-testid="subcategories-tab">Subcategories</TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => openCatModal()}
              className="bg-imperial hover:bg-imperial/90"
              data-testid="add-category-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => (
              <Card key={cat.id} className="card-luxury" data-testid={`category-${cat.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading text-lg font-medium">{cat.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{cat.description || '-'}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {subcategories.filter(s => s.category_id === cat.id).length} subcategories
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openCatModal(cat)}
                        data-testid={`edit-category-${cat.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteCatId(cat.id)}
                        data-testid={`delete-category-${cat.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Subcategories Tab */}
        <TabsContent value="subcategories" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => openSubModal()}
              className="bg-imperial hover:bg-imperial/90"
              data-testid="add-subcategory-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Subcategory
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subcategories.map((sub) => (
              <Card key={sub.id} className="card-luxury" data-testid={`subcategory-${sub.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading text-lg font-medium">{sub.name}</h3>
                      <p className="text-xs text-imperial mt-1">{sub.category_name}</p>
                      <p className="text-sm text-muted-foreground mt-1">{sub.description || '-'}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openSubModal(sub)}
                        data-testid={`edit-subcategory-${sub.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteSubId(sub.id)}
                        data-testid={`delete-subcategory-${sub.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Category Modal */}
      <Dialog open={showCatModal} onOpenChange={setShowCatModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCat ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Name *</Label>
              <Input
                value={catForm.name}
                onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                placeholder="e.g., Bride"
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Description</Label>
              <Input
                value={catForm.description}
                onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
                placeholder="Description"
                data-testid="category-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCatModal(false)}>Cancel</Button>
            <Button onClick={handleSaveCat} className="bg-imperial hover:bg-imperial/90" data-testid="save-category-button">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Modal */}
      <Dialog open={showSubModal} onOpenChange={setShowSubModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingSub ? 'Edit Subcategory' : 'Add Subcategory'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Category *</Label>
              <Select 
                value={subForm.category_id} 
                onValueChange={(value) => setSubForm({ ...subForm, category_id: value })}
              >
                <SelectTrigger data-testid="subcategory-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Name *</Label>
              <Input
                value={subForm.name}
                onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
                placeholder="e.g., Dress"
                data-testid="subcategory-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Description</Label>
              <Input
                value={subForm.description}
                onChange={(e) => setSubForm({ ...subForm, description: e.target.value })}
                placeholder="Description"
                data-testid="subcategory-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubModal(false)}>Cancel</Button>
            <Button onClick={handleSaveSub} className="bg-imperial hover:bg-imperial/90" data-testid="save-subcategory-button">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmations */}
      <AlertDialog open={!!deleteCatId} onOpenChange={() => setDeleteCatId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              This will also delete all subcategories under this category.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCat} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteSubId} onOpenChange={() => setDeleteSubId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subcategory?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSub} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoriesPage;
